import { markRaw, type Raw } from 'vue';
import { Node, Trigger } from '@keyvue/one-api-client';

import TriggerRealworksAgenda from '@/flow/flow/components/graph/custom-types/TriggerRealworksAgenda/CustomNode.vue';
import TriggerRealworksAgendaSettings from '@/flow/flow/components/graph/custom-types/TriggerRealworksAgenda/CustomSettings.vue';

import TriggerRealworksTaak from '@/flow/flow/components/graph/custom-types/TriggerRealworksTaak/CustomNode.vue';
import TriggerRealworksTaakSettings from '@/flow/flow/components/graph/custom-types/TriggerRealworksTaak/CustomSettings.vue';

import TriggerEmail from '@/flow/flow/components/graph/custom-types/TriggerEmail/CustomNode.vue';
import TriggerEmailSettings from '@/flow/flow/components/graph/custom-types/TriggerEmail/CustomSettings.vue';

import TriggerRealworksRelatie from '@/flow/flow/components/graph/custom-types/TriggerRealworksRelatie/CustomNode.vue';
import TriggerRealworksRelatieSettings from '@/flow/flow/components/graph/custom-types/TriggerRealworksRelatie/CustomSettings.vue';

import TriggerRealworksKenmerk from '@/flow/flow/components/graph/custom-types/TriggerRealworksKenmerk/CustomNode.vue';
import TriggerRealworksKenmerkSettings from '@/flow/flow/components/graph/custom-types/TriggerRealworksKenmerk/CustomSettings.vue';

import TriggerRealworksBirthday from '@/flow/flow/components/graph/custom-types/TriggerRealworksBirthday/CustomNode.vue';
import TriggerRealworksBirthdaySettings from '@/flow/flow/components/graph/custom-types/TriggerRealworksBirthday/CustomSettings.vue';

import NodeActionSendEmail from '@/flow/flow/components/graph/custom-types/NodeActionSendEmail/CustomNode.vue';
import NodeActionSendEmailSettings from '@/flow/flow/components/graph/custom-types/NodeActionSendEmail/CustomSettings.vue';

import NodeCancel from '@/flow/flow/components/graph/custom-types/NodeCancel/CustomNode.vue';
import NodeCancelSettings from '@/flow/flow/components/graph/custom-types/NodeCancel/CustomSettings.vue';

import NodeEnd from '@/flow/flow/components/graph/custom-types/NodeEnd/CustomNode.vue';
import NodeEndSettings from '@/flow/flow/components/graph/custom-types/NodeEnd/CustomSettings.vue';

import NodeWait from '@/flow/flow/components/graph/custom-types/NodeWait/CustomNode.vue';
import NodeWaitSettings from '@/flow/flow/components/graph/custom-types/NodeWait/CustomSettings.vue';

import NodeConditional from '@/flow/flow/components/graph/custom-types/NodeConditional/CustomNode.vue';

interface CustomTypeDefinition {
    nodeComponent: Raw<any>
    settingsComponent?: Raw<any>
    options?: any
}

const customTypes: Record<string, CustomTypeDefinition> = {
    'trigger_realworks-relatie': {
        nodeComponent: markRaw(TriggerRealworksRelatie),
        settingsComponent: markRaw(TriggerRealworksRelatieSettings),
    },
    'trigger_realworks-kenmerk': {
        nodeComponent: markRaw(TriggerRealworksKenmerk),
        settingsComponent: markRaw(TriggerRealworksKenmerkSettings),
    },
    'trigger_realworks-birthday': {
        nodeComponent: markRaw(TriggerRealworksBirthday),
        settingsComponent: markRaw(TriggerRealworksBirthdaySettings),
    },
    'trigger_realworks-agenda': {
        nodeComponent: markRaw(TriggerRealworksAgenda),
        settingsComponent: markRaw(TriggerRealworksAgendaSettings),
    },
    'trigger_realworks-taak': {
        nodeComponent: markRaw(TriggerRealworksTaak),
        settingsComponent: markRaw(TriggerRealworksTaakSettings),
    },
    'trigger_email': {
        nodeComponent: markRaw(TriggerEmail),
        settingsComponent: markRaw(TriggerEmailSettings),
    },
    'node_action_send-email': {
        nodeComponent: markRaw(NodeActionSendEmail),
        settingsComponent: markRaw(NodeActionSendEmailSettings),
    },
    'node_cancel': {
        nodeComponent: markRaw(NodeCancel),
        settingsComponent: markRaw(NodeCancelSettings),
    },
    'node_end': {
        nodeComponent: markRaw(NodeEnd),
        settingsComponent: markRaw(NodeEndSettings),
    },
    'node_wait': {
        nodeComponent: markRaw(NodeWait),
        settingsComponent: markRaw(NodeWaitSettings),
    },
    'node_conditional': {
        nodeComponent: markRaw(NodeConditional),
        options: {
            selectable: false,
        },
    },
};

/**
 * Map of custom types, define new ones here
 */
const customTypeNodeMap: Record<string, Raw<any>> = Object.fromEntries(
    Object.entries(customTypes).map(([key, value]) => [key, value.nodeComponent]),
);

const customTypeSettingsMap: Record<string, Raw<any>> = Object.fromEntries(
    Object.entries(customTypes).map(([key, value]) => [key, value.settingsComponent]),
);

/**
 * Parses the trigger or node and returns a descriptive type name
 */
function getCustomTypeName(triggerOrNode: Trigger | Node) {
    return [
        triggerOrNode instanceof Trigger ? 'trigger' : 'node',
        triggerOrNode.type.value.toLowerCase(),
        triggerOrNode.options?.type || null,
        triggerOrNode.options?.action || null,
    ].filter((part) => part).join('_');
}

function getCustomNodeOptions(customTypeName: string) {
    return customTypes[customTypeName]?.options || {};
}

export {
    customTypeNodeMap,
    customTypeSettingsMap,
    getCustomTypeName,
    getCustomNodeOptions,
};
